import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner} from 'react-bootstrap'
import { Typeahead } from 'react-bootstrap-typeahead'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import { JournalBookmark } from 'react-bootstrap-icons'
import {
  BsBoxArrowUpRight,
  BsEyeFill,
  BsFileEarmarkImage,
  BsFileEarmarkPdf,
  BsFunnelFill,
  BsPaperclip,
  BsPencilFill,
  BsSearch,
  BsThreeDotsVertical,
  BsTrashFill} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import toast from 'react-hot-toast'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'

import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import {
  buildHomeworkHeaders,
  deleteHomeworkRequest,
  fetchHomeworkList,
  getHomeworkErrorMessage,
  normalizeHomeworkRow,
  parseHomeworkListResponse,
  resolveInstituteId} from './commonComponents/homeworkApi'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import { getFilteredSubjects } from './admitCardHelpers'

const INITIAL_FILTERS = {
  search: '',
  course_id: '',
  class_id: '',
  subject_id: ''}

const resolveClassId = (cls) => cls?.class_id ?? cls?.classId ?? cls?.class?.id ?? cls?.id

const resolveCourseId = (cls) =>
  cls?.course_id ?? cls?.courseId ?? cls?.course?.id ?? ''

const normalizeClassOption = (cls) => {
  const classId = resolveClassId(cls)
  const courseId = resolveCourseId(cls)
  const className =
    cls?.class_name ?? cls?.className ?? cls?.name ?? cls?.class?.class_name ?? ''
  const courseName =
    typeof cls?.course === 'string'
      ? cls.course
      : cls?.course_name ?? cls?.courseName ?? cls?.course?.name ?? ''
  return {
    ...cls,
    id: classId,
    class_id: classId,
    class_name: className,
    course_id: courseId,
    course_name: courseName}
}

const parseAssignedClasses = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.map(normalizeClassOption).filter((c) => c.class_name && resolveClassId(c))
  }
  const list = raw.assigned_classes ?? raw.assignedClasses ?? raw.classes ?? []
  if (!Array.isArray(list)) return []
  return list.map(normalizeClassOption).filter((c) => c.class_name && resolveClassId(c))
}

const truncate = (text, len = 80) => {
  if (!text) return '—'
  const s = String(text)
  return s.length <= len ? s : `${s.slice(0, len)}…`
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'})
}

const formatDueDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'})
}

const statusBadge = (s) => {
  const v = String(s || '').toLowerCase()
  if (v === 'published') return <Badge bg="success">Published</Badge>
  if (v === 'draft') return <Badge bg="secondary">Draft</Badge>
  if (v === 'archived') return <Badge bg="warning" text="dark">Archived</Badge>
  return <Badge bg="light" text="dark">{s || '—'}</Badge>
}

const getAttachmentKind = (url, label) => {
  const hay = `${url || ''} ${label || ''}`.toLowerCase()
  if (/\.pdf(\?|$)/i.test(hay)) return 'pdf'
  if (/\.(jpe?g|png|gif|webp)(\?|$)/i.test(hay)) return 'image'
  return 'file'
}

const openAttachment = (url) => {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

function HomeworkAttachmentCell({ row }) {
  if (!row?.attachment_url) {
    return <span className="text-muted">—</span>
  }

  const label =
    row.media_label && row.media_label !== '—' ? row.media_label : 'Attachment'
  const kind = getAttachmentKind(row.attachment_url, label)
  const Icon =
    kind === 'pdf' ? BsFileEarmarkPdf : kind === 'image' ? BsFileEarmarkImage : BsPaperclip

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span
        className="d-inline-flex align-items-center gap-2 rounded-2 px-2 py-1"
        style={{
          backgroundColor: '#F3F4F6',
          border: '1px solid #E5E7EB',
          fontSize: '12px',
          color: '#374151',
          maxWidth: '160px',
          fontFamily: 'Inter, system-ui, sans-serif'}}
        title={label}
      >
        <Icon size={16} style={{ color: '#223843', flexShrink: 0 }} />
        <span className="text-truncate">{label}</span>
      </span>
      <Button
        type="button"
        variant="light"
        size="sm"
        className="d-inline-flex align-items-center gap-1 border"
        style={{
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#223843',
          padding: '4px 10px'}}
        onClick={() => openAttachment(row.attachment_url)}
        title="Open attachment in new tab"
      >
        <BsBoxArrowUpRight size={14} />
        Open
      </Button>
    </div>
  )
}

export default function Homework() {
  const { logout, user } = useAuth()
  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const normalizedRoles = (user?.roles || []).map((role) => String(role).toLowerCase())
  const isTeacherUser =
    normalizedUserType === 'teacher' || normalizedRoles.includes('teacher')
  const isAdminUser =
    normalizedUserType === 'admin' || normalizedRoles.includes('admin')
  const canAccessHomework = isTeacherUser || isAdminUser
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const location = useLocation()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS)
  const [showFilterCard, setShowFilterCard] = useState(true)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [viewTarget, setViewTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [courses, setCourses] = useState([])
  const [assignedClasses, setAssignedClasses] = useState([])
  const [allSubjects, setAllSubjects] = useState([])
  const [filterClasses, setFilterClasses] = useState([])
  const [filterSubjects, setFilterSubjects] = useState([])
  const [filtersLoading, setFiltersLoading] = useState(false)

  const loadHomework = useCallback(async (filterState) => {
    const instituteId = resolveInstituteId()
    if (!instituteId) {
      setRows([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const params = {}
      if (filterState.course_id) params.course_id = Number(filterState.course_id)
      if (filterState.class_id) params.class_id = Number(filterState.class_id)
      if (filterState.subject_id) params.subject_id = Number(filterState.subject_id)

      const res = await fetchHomeworkList(params)
      const list = parseHomeworkListResponse(res).map(normalizeHomeworkRow)
      setRows(list)
    } catch (err) {
      toast.error(getHomeworkErrorMessage(err))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadHomework(appliedFilters)
  }, [location.pathname, location.key, appliedFilters, loadHomework])

  useEffect(() => {
    let cancelled = false

    const loadFilterMeta = async () => {
      setFiltersLoading(true)
      try {
        const headers = buildHomeworkHeaders()
        const requests = [
          ApiEndpoints.getAllCourses(),
          ApiEndpoints.getAllSubjects(),
        ]
        if (!isAdminUser) {
          requests.unshift(axios.get(`${BASE_URL}/teacher-classes`, { headers }))
        }

        const results = await Promise.all(requests)
        if (cancelled) return

        let classRes = null
        let courseRes
        let subjectRes
        if (!isAdminUser) {
          ;[classRes, courseRes, subjectRes] = results
        } else {
          ;[courseRes, subjectRes] = results
        }

        const courseList = courseRes?.data?.data ?? courseRes?.data ?? []
        setCourses(Array.isArray(courseList) ? courseList : [])

        const subjectList = subjectRes?.data?.data ?? subjectRes?.data ?? []
        setAllSubjects(Array.isArray(subjectList) ? subjectList : [])

        if (!isAdminUser) {
          let parsed = parseAssignedClasses(classRes?.data?.data ?? classRes?.data)
          if (parsed.length === 0) {
            const teacherId = user?.id ?? user?.teacher_id
            if (teacherId) {
              try {
                const altRes = await axios.get(`${BASE_URL}/teacher-classes/${teacherId}`, {
                  headers})
                parsed = parseAssignedClasses(altRes?.data?.data ?? altRes?.data)
              } catch {
                /* keep empty */
              }
            }
          }
          if (!cancelled) setAssignedClasses(parsed)
        } else {
          setAssignedClasses([])
        }
      } catch {
        if (!cancelled) {
          setCourses([])
          setAllSubjects([])
          setAssignedClasses([])
        }
      } finally {
        if (!cancelled) setFiltersLoading(false)
      }
    }

    loadFilterMeta()
    return () => {
      cancelled = true
    }
  }, [isAdminUser, user?.id, user?.teacher_id])

  useEffect(() => {
    if (!filters.course_id) {
      setFilterClasses([])
      return
    }

    let cancelled = false
    const courseId = filters.course_id

    const loadClasses = async () => {
      if (isAdminUser) {
        try {
          const res = await ApiEndpoints.getAllClassesByCourseId(courseId)
          if (cancelled) return
          const list = res?.data?.data ?? res?.data ?? []
          setFilterClasses(
            (Array.isArray(list) ? list : [])
              .filter((c) => c?.id != null)
              .map((c) =>
                normalizeClassOption({
                  ...c,
                  course_id: courseId}),
              ),
          )
        } catch {
          if (!cancelled) setFilterClasses([])
        }
        return
      }

      const matched = assignedClasses.filter(
        (c) => String(resolveCourseId(c)) === String(courseId),
      )
      if (!cancelled) setFilterClasses(matched)
    }

    loadClasses()
    return () => {
      cancelled = true
    }
  }, [filters.course_id, isAdminUser, assignedClasses])

  useEffect(() => {
    if (!filters.course_id || !filters.class_id) {
      setFilterSubjects([])
      return
    }

    let cancelled = false
    const courseId = filters.course_id
    const classId = filters.class_id

    const loadSubjects = async () => {
      try {
        const res = await ApiEndpoints.getSubjectsByCourse(courseId, {
          headers: buildHomeworkHeaders()})
        if (cancelled) return
        const byCourse = res?.data?.data ?? []
        if (Array.isArray(byCourse) && byCourse.length > 0) {
          const filtered = getFilteredSubjects(byCourse, courseId, classId)
          setFilterSubjects(filtered.length > 0 ? filtered : byCourse)
          return
        }
      } catch {
        /* fall through */
      }

      if (cancelled) return
      const filtered = getFilteredSubjects(allSubjects, courseId, classId)
      if (filtered.length > 0) {
        setFilterSubjects(filtered)
        return
      }
      setFilterSubjects(
        allSubjects.filter((s) => String(s?.course_id) === String(courseId)),
      )
    }

    loadSubjects()
    return () => {
      cancelled = true
    }
  }, [filters.course_id, filters.class_id, allSubjects])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const filteredRows = useMemo(() => {
    if (!appliedFilters.search) return rows
    const q = appliedFilters.search.toLowerCase()
    return rows.filter((r) => {
      const hay = [
        r.title,
        r.description,
        r.class_name,
        r.class_name_only,
        r.subject_name,
        r.teacher_name,
        r.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [rows, appliedFilters.search])

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage) || 1
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage
  const indexOfLastItem = currentPage * itemsPerPage
  const displayRows = useMemo(
    () => filteredRows.slice(indexOfFirstItem, indexOfLastItem),
    [filteredRows, indexOfFirstItem, indexOfLastItem],
  )

  useEffect(() => {
    if (filteredRows.length === 0) return
    const tp = Math.ceil(filteredRows.length / itemsPerPage) || 1
    if (currentPage > tp) setCurrentPage(tp)
  }, [filteredRows.length, itemsPerPage, currentPage])

  const courseOptions = useMemo(() => {
    if (isAdminUser) return courses
    const ids = new Set(
      assignedClasses.map((c) => String(resolveCourseId(c))).filter(Boolean),
    )
    return courses.filter((c) => ids.has(String(c.id)))
  }, [courses, assignedClasses, isAdminUser])

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => {
      const next = { ...prev, [name]: value }
      if (name === 'course_id') {
        next.class_id = ''
        next.subject_id = ''
      }
      if (name === 'class_id') {
        next.subject_id = ''
      }
      return next
    })
  }

  const applyFilters = () => {
    setAppliedFilters({ ...filters })
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS)
    setAppliedFilters(INITIAL_FILTERS)
    setCurrentPage(1)
  }

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return
    setDeleting(true)
    try {
      const res = await deleteHomeworkRequest(deleteTarget.id)
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id))
      if (viewTarget?.id === deleteTarget.id) setViewTarget(null)
      toast.success(res?.data?.message || 'Homework deleted successfully.')
      setDeleteTarget(null)
    } catch (err) {
      toast.error(getHomeworkErrorMessage(err))
    } finally {
      setDeleting(false)
    }
  }

  if (!canAccessHomework) {
    return <Navigate to="/Dashboard" replace />
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .table td {
            overflow: visible !important;
            user-select: text;
          }
          .table tbody tr {
            overflow: visible !important;
            cursor: default;
          }
          .table-card {
            overflow: visible !important;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
          }
          .table td, .table th { user-select: text; }
          .hw-filter-select {
            border-radius: 8px;
            border: 1px solid #D1D3D4;
            font-size: 14px;
            height: 40px;
            transition: all 0.2s ease;
            background-color: #ffffff;
            font-family: Inter, system-ui, sans-serif;
          }
          /* Bootstrap btn-close already draws an X; hide Typeahead's extra × text */
          .homework-filter-typeahead .rbt-close-content {
            display: none !important;
          }
          .homework-filter-typeahead .rbt-aux .rbt-close {
            opacity: 0.65;
            box-shadow: none;
            background-size: 0.65em;
          }
          .homework-filter-typeahead .rbt-aux .rbt-close:hover {
            opacity: 1;
          }
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }
          .dropdown-menu {
            z-index: 9999 !important;
            position: absolute !important;
          }
          .table {
            overflow: visible !important;
          }
          @media (max-width: 768px) {
            .hw-desc-cell {
              max-width: none !important;
              white-space: normal !important;
              word-break: break-word !important;
              overflow-wrap: anywhere !important;
              line-height: 1.45 !important;
            }
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px'}}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px'}}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0}}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title text-start"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2'}}
                  >
                    Homework
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}
                  >
                    View and manage homework assignments for your classes.
                  </p>
                </div>
              </div>

              {!isMobile && (
                <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                  <ProfileDropdown logout={logout} notificationCount={3} showNotifications={true} />
                </div>
              )}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span
                  style={{
                    color: '#6b7280',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value, 10))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  <option value="100">100 per page</option>
                  <option value="200">200 per page</option>
                  <option value="300">300 per page</option>
                </Form.Select>
              </div>
              <button
                type="button"
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'}}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>

            {!isAdminUser && (
              <button
              type="button"
              className="d-none d-md-flex align-items-center justify-content-center"
              onClick={() => navigate('/homework/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 14px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                gap: '6px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Add New"
            >
              <JournalBookmark size={18} />
              <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Add New Homework
              </span>
            </button>
            )}
          </div>

          <div className="d-md-none d-flex justify-content-end mb-4">
            <button
              type="button"
              onClick={() => navigate('/homework/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 14px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'}}
            >
              <JournalBookmark size={18} />
              <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Add New Homework
              </span>
            </button>
          </div>

          <div
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
              position: 'relative',
              zIndex: showFilterCard ? 10 : 1}}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  overflow: 'visible',
                  position: 'relative'}}
              >
                <div style={{ padding: '20px', borderBottom: '1px solid #F3F4F6' }}>
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#223843',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'}}
                    >
                      <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                    </div>
                    <div className="text-start">
                      <h5 className="m-0 fw-semibold" style={{ fontSize: '16px', color: '#111827' }}>
                        Search &amp; Filter Homework
                      </h5>
                      <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                        Select course, class, and subject to filter homework
                      </p>
                    </div>
                  </div>
                </div>
                <Card.Body style={{ overflow: 'visible', position: 'relative' }}>
                  <Row className="g-3 align-items-end text-start">
                    <Col xs={12} md={6} lg={3}>
                      <Form.Label>Course</Form.Label>
                      <div className="homework-filter-typeahead">
                        <Typeahead
                          id="homework-filter-course"
                          labelKey="name"
                          clearButton
                          isLoading={filtersLoading}
                          disabled={filtersLoading}
                          onChange={(selected) => {
                            const course = selected[0]
                            handleFilterChange({
                              target: {
                                name: 'course_id',
                                value: course ? String(course.id) : ''}})
                          }}
                          options={courseOptions}
                          placeholder={
                            filtersLoading ? 'Loading courses…' : 'All courses'
                          }
                          emptyLabel="No courses found"
                          selected={courseOptions.filter(
                            (c) => String(c.id) === String(filters.course_id),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif'}}}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                      <Form.Label>Class</Form.Label>
                      <div className="homework-filter-typeahead">
                        <Typeahead
                          id="homework-filter-class"
                          labelKey="class_name"
                          clearButton
                          isLoading={filtersLoading && !!filters.course_id}
                          disabled={!filters.course_id || filtersLoading}
                          onChange={(selected) => {
                            const cls = selected[0]
                            handleFilterChange({
                              target: {
                                name: 'class_id',
                                value: cls ? String(resolveClassId(cls)) : ''}})
                          }}
                          options={filterClasses}
                          placeholder={
                            !filters.course_id
                              ? 'Select course first'
                              : filterClasses.length === 0
                                ? 'No classes'
                                : 'All classes'
                          }
                          emptyLabel="No classes found"
                          selected={filterClasses.filter(
                            (c) => String(resolveClassId(c)) === String(filters.class_id),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif'}}}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                      <Form.Label>Subject</Form.Label>
                      <div className="homework-filter-typeahead">
                        <Typeahead
                          id="homework-filter-subject"
                          labelKey={(s) =>
                            s.subject_name || s.name || `Subject ${s.id}`
                          }
                          clearButton
                          isLoading={filtersLoading && !!filters.class_id}
                          disabled={!filters.class_id || filtersLoading}
                          onChange={(selected) => {
                            const subject = selected[0]
                            handleFilterChange({
                              target: {
                                name: 'subject_id',
                                value: subject ? String(subject.id) : ''}})
                          }}
                          options={filterSubjects}
                          placeholder={
                            !filters.class_id
                              ? 'Select class first'
                              : filterSubjects.length === 0
                                ? 'No subjects'
                                : 'All subjects'
                          }
                          emptyLabel="No subjects found"
                          selected={filterSubjects.filter(
                            (s) => String(s.id) === String(filters.subject_id),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif'}}}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                      <div className="d-flex gap-2 flex-wrap justify-content-lg-end justify-content-start">
                        <Button variant="outline-secondary" onClick={resetFilters} style={{ borderRadius: '8px' }}>
                          Reset
                        </Button>
                        <Button
                          onClick={applyFilters}
                          style={{
                            backgroundColor: '#223843',
                            border: 'none',
                            borderRadius: '8px',
                            minWidth: '100px'}}
                        >
                          Apply
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          <Card
            className="table-card"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'visible',
              backgroundColor: '#ffffff'}}
          >
            <div className="table-wrapper">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span className="text-muted" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Loading homework…
                  </span>
                </div>
              ) : filteredRows.length === 0 ? (
                <div
                  className="text-center text-muted py-5 px-3"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  {rows.length === 0
                    ? 'No homework yet. Create one to get started.'
                    : 'No homework entries match your filters.'}
                </div>
              ) : (
                <Table className="table mb-0">
                  <Thead>
                    <Tr style={{ backgroundColor: '#F3F4F6' }}>
                      {[
                        'Title',
                        'Class',
                        'Subject',
                        // 'Description',
                        'Due date',
                        'Attachment',
                        'Status',
                        // 'Created',
                        'Action',
                      ].map((col) => (
                        <Th
                          key={col}
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            padding: '16px',
                            border: 'none',
                            borderBottom: '1px solid #D1D3D4',
                            textAlign: col === 'Action' ? 'center' : 'left',
                            color: '#111827',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: col === 'Action' ? 'nowrap' : undefined}}
                        >
                          {col}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody className='text-start'>
                    {displayRows.map((r, index) => (
                      <Tr
                        key={r.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          borderBottom: '1px solid #E5E7EB'}}
                      >
                        <Td style={{ padding: '16px', fontWeight: 600, minWidth: '140px' }}>
                          {r.title}
                        </Td>
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {r.class_name_only || '—'}
                        </Td>
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {r.subject_name || '—'}
                        </Td>
                        {/* <Td className="hw-desc-cell" style={{ padding: '16px', maxWidth: '240px' }}>
                          {truncate(r.description)}
                        </Td> */}
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {formatDueDate(r.due_date)}
                        </Td>
                        <Td style={{ padding: '16px', minWidth: '200px' }}>
                          <HomeworkAttachmentCell row={r} />
                        </Td>
                        <Td style={{ padding: '16px' }}>{statusBadge(r.status)}</Td>
                        {/* <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {formatDateTime(r.created_at)}
                        </Td> */}
                        <Td className="text-center" style={{ padding: '16px' }}>
                          <div className="d-none d-md-block">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="light"
                                className="dropdown-toggle-no-caret"
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  border: '1px solid #D1D3D4',
                                  padding: 0}}
                              >
                                <BsThreeDotsVertical />
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{ borderRadius: '8px', minWidth: '160px' }}>
                                <Dropdown.Item onClick={() => setViewTarget(r)}>
                                  <BsEyeFill className="me-2" /> View
                                </Dropdown.Item>
                                {!isAdminUser && (  
                                  <>
                                <Dropdown.Item
                                  onClick={() => navigate(`/homework/edit/${r.id}`)}
                                >
                                  <BsPencilFill className="me-2" /> Update Homework
                                </Dropdown.Item>
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() =>
                                    setDeleteTarget({ id: r.id, title: r.title })
                                  }
                                >
                                  <BsTrashFill className="me-2" /> Delete Homework
                                </Dropdown.Item>
                                </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                          <div className="d-md-none d-flex gap-2 justify-content-center">
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{
                                background: '#223843',
                                color: '#fff',
                                borderRadius: '50%',
                                width: 36,
                                height: 36}}
                              onClick={() => setViewTarget(r)}
                            >
                              <BsEyeFill size={14} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{
                                background: '#10B981',
                                color: '#fff',
                                borderRadius: '50%',
                                width: 36,
                                height: 36}}
                              onClick={() => navigate(`/homework/edit/${r.id}`)}
                              title="Update Homework"
                            >
                              <BsPencilFill size={14} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{
                                background: '#dc2626',
                                color: '#fff',
                                borderRadius: '50%',
                                width: 36,
                                height: 36}}
                              onClick={() =>
                                setDeleteTarget({ id: r.id, title: r.title })
                              }
                              title="Delete Homework"
                            >
                              <BsTrashFill size={14} />
                            </button>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </div>
          </Card>

          {!loading && filteredRows.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              totalItems={filteredRows.length}
              itemLabel="homework entries"
              className="d-flex justify-content-between align-items-center gap-3 mb-3 mt-4 flex-wrap"
            />
          )}
        </Container>
      </div>

      <Modal show={Boolean(viewTarget)} onHide={() => setViewTarget(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            {viewTarget?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-start">
          <p>
            <strong>Class:</strong> {viewTarget?.class_name_only || viewTarget?.class_name || '—'}
          </p>
          <p>
            <strong>Subject:</strong> {viewTarget?.subject_name || '—'}
          </p>
          <p>
            <strong>Teacher:</strong> {viewTarget?.teacher_name || '—'}
          </p>
          <p>
            <strong>Status:</strong> {viewTarget?.status}
          </p>
          <p>
            <strong>Due date:</strong> {formatDueDate(viewTarget?.due_date)}
          </p>
          <p>
            <strong>Description:</strong>
            <br />
            {viewTarget?.description || '—'}
          </p>
          <div className="mb-3">
            <strong className="d-block mb-2">Attachment</strong>
            {viewTarget?.attachment_url ? (
              <HomeworkAttachmentCell row={viewTarget} />
            ) : (
              '—'
            )}
          </div>
          <p className="text-muted small mb-0">
            Created: {formatDateTime(viewTarget?.created_at)}
            {viewTarget?.updated_at ? ` · Updated: ${formatDateTime(viewTarget.updated_at)}` : ''}
          </p>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(deleteTarget)}
        onHide={() => !deleting && setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete homework</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remove <strong>{deleteTarget?.title}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting…
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
